import React, { useState, useEffect } from 'react';
import api from './api';
import { useToast } from './Toast';
import mercariCategories from './data/mercari-categories.json';
import './TaskList.css';

const TASK_TARGETS = {
  keyword: {
    label: '关键词',
    placeholder: '请输入关键词'
  },
  seller: {
    label: '卖家',
    placeholder: '获取后自动填入卖家编号'
  }
};

const PRICE_RANGE_LABEL = '日元区间';
const EXCLUDE_KEYWORD_LABEL = '屏蔽词';
const EXCLUDE_KEYWORD_PLACEHOLDER = '多个屏蔽词之间用空格分开';
const UNSET_VALUE = '未设置';
const NO_CATEGORY_VALUE = '';
const DEFAULT_WEBSITE = 'mercari';
const SUPPORTED_WEBSITES = ['mercari', 'rakuma', 'paypay'];
const WEBSITE_LABELS = {
  mercari: '煤炉',
  rakuma: '乐天',
  paypay: '雅虎闲置'
};
const WEBSITE_ICONS = {
  mercari: 'https://xin-feng.xyz/user-system-assets/mercari.png',
  rakuma: 'https://xin-feng.xyz/user-system-assets/rakuma.png',
  paypay: 'https://xin-feng.xyz/user-system-assets/paypay.png'
};
const SELLER_ITEM_ID_HELP_TEXT = {
  mercari: '商品编号规则：https://jp.mercari.com/item/m36309707486 => m36309707486',
  rakuma: '商品编号规则：https://item.fril.jp/cc46e379267f029e8323607adf39befc => cc46e379267f029e8323607adf39befc',
  paypay: '商品编号规则：https://paypayfleamarket.yahoo.co.jp/item/z704234584 => z704234584'
};
const QR_MODAL_CONTENT = {
  purchase: {
    title: '购买券码',
    description: '请保存图片到相册，打开闲鱼扫一扫。',
    imageSrc: 'https://xin-feng.xyz/xianyu-assets/purchase.jpg',
    imageAlt: '购买券码二维码'
  },
  support: {
    title: '联系客服',
    description: '请保存图片到相册，打开闲鱼扫一扫。',
    imageSrc: 'https://xin-feng.xyz/xianyu-assets/consult.jpg',
    imageAlt: '联系客服二维码'
  }
};
const normalizeTaskType = (taskType) => (
  Object.prototype.hasOwnProperty.call(TASK_TARGETS, taskType) ? taskType : 'keyword'
);

const getWebsiteKey = (website) => (
  website === undefined || website === null ? '' : String(website)
);

const getWebsiteLabel = (website) => {
  const websiteText = getWebsiteKey(website);
  return WEBSITE_LABELS[websiteText] || websiteText || UNSET_VALUE;
};

const getWebsiteIconSrc = (website) => WEBSITE_ICONS[getWebsiteKey(website)] || '';

const supportsCategory = (website) => {
  const websiteKey = getWebsiteKey(website);
  return !websiteKey || websiteKey === 'mercari';
};

const supportsExcludeKeyword = (website) => getWebsiteKey(website) !== 'paypay';

const WebsiteIcon = ({ website, className = '' }) => {
  const iconSrc = getWebsiteIconSrc(website);
  if (!iconSrc) return null;

  return (
    <img
      className={`website-icon ${className}`.trim()}
      src={iconSrc}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
    />
  );
};

const WebsiteLabel = ({ website, className = '' }) => {
  const label = getWebsiteLabel(website);

  return (
    <span className={`website-label ${className}`.trim()}>
      <WebsiteIcon website={website} />
      {label}
    </span>
  );
};

const getWebsiteTabs = (tasks) => {
  const tabsByKey = new Map();

  SUPPORTED_WEBSITES.forEach((website) => {
    tabsByKey.set(website, {
      key: website,
      label: getWebsiteLabel(website)
    });
  });

  tasks.forEach((task) => {
    const key = getWebsiteKey(task.website);
    if (!tabsByKey.has(key)) {
      tabsByKey.set(key, {
        key,
        label: getWebsiteLabel(task.website)
      });
    }
  });

  const tabs = Array.from(tabsByKey.values());
  const defaultIndex = tabs.findIndex(tab => tab.key === DEFAULT_WEBSITE);
  if (defaultIndex > 0) {
    const [defaultTab] = tabs.splice(defaultIndex, 1);
    tabs.unshift(defaultTab);
  }

  return tabs;
};

const getCouponWebsiteOptions = (tasks) => {
  const websites = new Set(SUPPORTED_WEBSITES);
  tasks.forEach((task) => {
    const website = getWebsiteKey(task.website);
    if (website) websites.add(website);
  });
  return Array.from(websites);
};

const chooseActiveWebsite = (tasks, currentWebsite) => {
  const tabs = getWebsiteTabs(tasks);
  if (tabs.length === 0) return null;

  if (currentWebsite !== null && tabs.some(tab => tab.key === currentWebsite)) {
    return currentWebsite;
  }

  const taskWebsites = tasks.map(task => getWebsiteKey(task.website));
  if (taskWebsites.includes(DEFAULT_WEBSITE)) {
    return DEFAULT_WEBSITE;
  }
  if (taskWebsites.length > 0) {
    return taskWebsites[0];
  }

  return DEFAULT_WEBSITE;
};

const formatTasks = (tasks) => (
  tasks.map(task => ({
    ...task,
    website: task.website,
    taskType: normalizeTaskType(task.task_type || task.taskType),
    seller: task.seller || '',
    excludeKeyword: task.exclude_keyword || task.excludeKeyword || '',
    minPrice: task.min_price,
    maxPrice: task.max_price,
    categoryId: normalizeCategoryId(task.category_id ?? task.categoryId)
  }))
);

const getSellerIdFromResponse = (data) => {
  const sellerId = data?.data?.id ?? data?.sellerId ?? data?.seller_id;
  return sellerId === undefined || sellerId === null ? '' : String(sellerId);
};

const normalizeCategoryId = (categoryId) => {
  if (categoryId === undefined || categoryId === null || categoryId === '') return null;
  const parsed = Number(categoryId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const findCategoryPath = (categories, categoryId, path = []) => {
  const normalizedCategoryId = normalizeCategoryId(categoryId);
  if (!normalizedCategoryId) return [];

  for (const category of categories) {
    const nextPath = [...path, category];
    if (category.id === normalizedCategoryId) {
      return nextPath;
    }

    const childPath = findCategoryPath(category.children || [], normalizedCategoryId, nextPath);
    if (childPath.length) {
      return childPath;
    }
  }

  return [];
};

const formatCategoryPath = (categoryId) => {
  const path = findCategoryPath(mercariCategories, categoryId);
  return path.length ? path.map(category => category.name).join(' / ') : '未设置';
};

function CategoryDropdown({ label, value, placeholder, options, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const normalizedValue = normalizeCategoryId(value);
  const selectedOption = options.find(option => option.id === normalizedValue);
  const displayValue = selectedOption?.name || placeholder;

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="category-select-field" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setIsOpen(false);
      }
    }}>
      <span className="category-select-field-label">{label}</span>
      <button
        type="button"
        className={`category-select-trigger${isOpen ? ' open' : ''}`}
        aria-label={`${label}：${displayValue}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        <span className={selectedOption ? 'category-select-value' : 'category-select-placeholder-text'}>
          {displayValue}
        </span>
        <span className="category-select-chevron" aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="category-select-menu" role="listbox" aria-label={label}>
          <button
            type="button"
            role="option"
            aria-selected={!selectedOption}
            className={`category-select-option category-select-option-placeholder${!selectedOption ? ' selected' : ''}`}
            onClick={() => handleSelect(NO_CATEGORY_VALUE)}
          >
            {placeholder}
          </button>
          {options.map(option => (
            <button
              key={option.id}
              type="button"
              role="option"
              aria-selected={option.id === normalizedValue}
              className={`category-select-option${option.id === normalizedValue ? ' selected' : ''}`}
              onClick={() => handleSelect(option.id)}
            >
              {option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskList() {
  const { addToast } = useToast();
  const EXPIRING_SOON_THRESHOLD = 3 * 24 * 60 * 60 * 1000;
  // 任务数据，初始为空数组
  const [tasks, setTasks] = useState([]);
  const [activeWebsite, setActiveWebsite] = useState(null);
  // 加载状态
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editTask, setEditTask] = useState({
    website: '',
    taskType: 'keyword',
    keyword: '',
    seller: '',
    excludeKeyword: '',
    minPrice: '',
    maxPrice: '',
    categoryId: null,
    speed: '3'
  });
  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponWebsite, setCouponWebsite] = useState(DEFAULT_WEBSITE);
  const [isQuerying, setIsQuerying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [sellerItemId, setSellerItemId] = useState('');
  const [isFetchingSeller, setIsFetchingSeller] = useState(false);
  const [currentRenewalTask, setCurrentRenewalTask] = useState(null);
  const [qrModalType, setQrModalType] = useState(null);

  useEffect(() => {
    if (!isEditModalOpen && !isCouponModalOpen && !qrModalType) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isEditModalOpen, isCouponModalOpen, qrModalType]);

  // 在组件挂载时获取任务数据
  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const response = await api.get('/api/tasks');
        if (response.data.tasks) {
          const formattedTasks = formatTasks(response.data.tasks);
          setTasks(formattedTasks);
          setActiveWebsite(currentWebsite => chooseActiveWebsite(formattedTasks, currentWebsite));
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, []);

  const refreshTasks = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await api.get('/api/tasks');
      if (response.data.tasks) {
        const formattedTasks = formatTasks(response.data.tasks);
        setTasks(formattedTasks);
        setActiveWebsite(currentWebsite => chooseActiveWebsite(formattedTasks, currentWebsite));
      }
    } catch (error) {
      } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleEdit = (task) => {
    const taskType = normalizeTaskType(task.taskType || task.task_type);
    setEditingTask(task);
    setEditTask({
      website: getWebsiteKey(task.website),
      taskType,
      keyword: task.keyword || task.keywords || '', // 兼容旧数据
      seller: task.seller || '',
      excludeKeyword: task.excludeKeyword || task.exclude_keyword || '',
      minPrice: task.minPrice,
      maxPrice: task.maxPrice,
      categoryId: normalizeCategoryId(task.categoryId ?? task.category_id),
      speed: task.speed || task.interval || task.queryInterval // 兼容旧数据
    });
    setSellerItemId('');
    setIsEditModalOpen(true);
  };

  const handleFetchSellerId = async () => {
    const itemId = sellerItemId.trim();
    if (!itemId) {
      addToast('请输入商品编号！', 'error');
      return;
    }

    setIsFetchingSeller(true);
    try {
      const response = await api.post('/api/tasks/seller-id', {
        itemId,
        website: editTask.website || DEFAULT_WEBSITE
      });
      const sellerId = getSellerIdFromResponse(response.data);
      if (!sellerId) {
        addToast('未能从该商品中识别卖家编号！', 'error');
        return;
      }
      setEditTask({ ...editTask, taskType: 'seller', seller: sellerId });
      addToast('卖家编号已获取', 'success');
    } catch (error) {
      addToast(error.response?.data?.error || '获取卖家编号失败，请稍后重试！', 'error');
    } finally {
      setIsFetchingSeller(false);
    }
  };

  const handleCouponExchange = (task = null) => {
    const nextWebsite = task
      ? getWebsiteKey(task.website) || DEFAULT_WEBSITE
      : activeWebsite || DEFAULT_WEBSITE;
    setCouponCode('');
    setCouponInfo(null);
    setCouponError('');
    setCouponWebsite(nextWebsite);
    setCurrentRenewalTask(task || null);
    setIsCouponModalOpen(true);
  };

  const handleQueryCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('请输入券码！');
      return;
    }

    setCouponError('');
    setCouponInfo(null);
    setIsQuerying(true);

    try {
      const response = await api.post('/api/coupons/query', { couponCode: couponCode.trim() });
      const coupon = response.data;

      if (currentRenewalTask && currentRenewalTask.id) {
        const taskSpeed = currentRenewalTask.speed || currentRenewalTask.interval || currentRenewalTask.queryInterval;
        if (String(coupon.speed) !== String(taskSpeed)) {
          setCouponError(`该券码速度为 ${coupon.speed} 秒，与任务速度 ${taskSpeed} 秒不匹配！`);
          setIsQuerying(false);
          return;
        }
      }

      setCouponInfo(coupon);
    } catch (error) {
      setCouponError(error.response?.data?.error || '查询失败，请稍后重试！');
    } finally {
      setIsQuerying(false);
    }
  };

  const handleSaveCoupon = async () => {
    if (!couponInfo) {
      setCouponError('请先查询券码！');
      return;
    }

    setCouponError('');
    setIsRedeeming(true);

    try {
      const requestData = { couponCode: couponInfo.couponCode };
      if (currentRenewalTask) {
        requestData.taskId = currentRenewalTask.id;
      } else {
        requestData.website = couponWebsite;
      }

      const response = await api.post('/api/coupons/redeem', requestData);

      await refreshTasks(false);

      if (currentRenewalTask) {
        setCurrentRenewalTask(null);
      } else {
        setActiveWebsite(couponWebsite);
      }

      setIsCouponModalOpen(false);
      setIsRedeeming(false);
      setCouponInfo(null);
      setCouponCode('');
      addToast(response.data.message, 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.error || '兑换失败，请稍后重试！';
      setCouponError(errorMessage);
      setIsRedeeming(false);
    }
  };

  const handleSave = async () => {
    const taskTarget = TASK_TARGETS[editTask.taskType];
    const activeValue = editTask[editTask.taskType]?.trim();

    if (!activeValue || !editTask.speed) {
      addToast(`请填写${taskTarget.label}和查询间隔！`, 'error');
      return;
    }

    if (editTask.taskType === 'keyword') {
      // 如果填写了价格，则验证价格是否为数字、非负数
      if (editTask.minPrice) {
        if (isNaN(editTask.minPrice)) {
          addToast('最低价格必须为数字！', 'error');
          return;
        }
        if (parseFloat(editTask.minPrice) < 0) {
          addToast('最低价格不能为负数！', 'error');
          return;
        }
      }

      if (editTask.maxPrice) {
        if (isNaN(editTask.maxPrice)) {
          addToast('最高价格必须为数字！', 'error');
          return;
        }
        if (parseFloat(editTask.maxPrice) < 0) {
          addToast('最高价格不能为负数！', 'error');
          return;
        }
      }

      // 如果同时填写了最低价格和最高价格，则验证最低价格小于最高价格
      if (editTask.minPrice && editTask.maxPrice) {
        if (parseFloat(editTask.minPrice) >= parseFloat(editTask.maxPrice)) {
          addToast('最低价格必须小于最高价格！', 'error');
          return;
        }
      }
    }

    setIsSaving(true);
    try {
      const response = await api.put(`/api/tasks/${editingTask.id}`, {
        taskType: editTask.taskType,
        keyword: editTask.keyword.trim(),
        seller: editTask.seller.trim(),
        excludeKeyword: editTask.excludeKeyword.trim(),
        minPrice: editTask.minPrice,
        maxPrice: editTask.maxPrice,
        categoryId: editTask.categoryId,
        speed: editTask.speed
      });
      if (response.data.message) {
        refreshTasks(false);
        setIsEditModalOpen(false);
      } else {
        addToast('更新任务失败：' + response.data.error, 'error');
      }
    } catch (error) {
      if (!error.response || error.response.status !== 401) {
        addToast('更新任务失败，请稍后重试！', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatExpiredAt = (expiredAt) => {
    if (!expiredAt) return '';

    if (typeof expiredAt === 'string' && expiredAt.includes(' ')) {
      const [datePart, timePart] = expiredAt.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute] = timePart.split(':');
      return `${year}年${month}月${day}日 ${hour}:${minute}`;
    }

    return expiredAt;
  };

  const getExpiredDate = (expiredAt) => {
    if (!expiredAt) return null;

    if (expiredAt instanceof Date) {
      return expiredAt;
    }

    if (typeof expiredAt === 'string') {
      const match = expiredAt.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
      if (match) {
        const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second)
        );
      }
    }

    const parsedDate = new Date(expiredAt);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const isTaskExpired = (expiredAt) => {
    const expiredDate = getExpiredDate(expiredAt);
    return expiredDate ? expiredDate.getTime() <= Date.now() : false;
  };

  const isTaskExpiringSoon = (expiredAt) => {
    const expiredDate = getExpiredDate(expiredAt);
    if (!expiredDate) return false;

    const timeUntilExpired = expiredDate.getTime() - Date.now();
    return timeUntilExpired > 0 && timeUntilExpired <= EXPIRING_SOON_THRESHOLD;
  };

  const getTaskStatus = (task) => {
    const isExpired = isTaskExpired(task.expired_at);
    const isExpiringSoon = !isExpired && isTaskExpiringSoon(task.expired_at);

    if (isExpiringSoon) {
      return {
        cardClassName: ' task-card-expiring',
        badgeClassName: ' task-status-badge-expiring',
        label: '即将到期',
        sortOrder: 0
      };
    }

    if (isExpired) {
      return {
        cardClassName: ' task-card-expired',
        badgeClassName: ' task-status-badge-expired',
        label: '已到期',
        sortOrder: 2
      };
    }

    return {
      cardClassName: '',
      badgeClassName: ' task-status-badge-running',
      label: '运行中',
      sortOrder: 1
    };
  };

  const hasTaskValue = (value) => value !== undefined && value !== null && value !== '';

  const formatPriceRange = (task) => {
    if (hasTaskValue(task.minPrice) && hasTaskValue(task.maxPrice)) return `¥${task.minPrice}-${task.maxPrice}`;
    if (hasTaskValue(task.minPrice)) return `¥${task.minPrice}以上`;
    if (hasTaskValue(task.maxPrice)) return `¥${task.maxPrice}以下`;
    return UNSET_VALUE;
  };

  const formatQueryInterval = (task) => {
    const interval = task.speed || task.interval || task.queryInterval;
    return hasTaskValue(interval) ? `${interval}秒` : UNSET_VALUE;
  };

  const getCategoryId = (task) => normalizeCategoryId(task.categoryId ?? task.category_id);
  const getExcludeKeyword = (task) => task.excludeKeyword || task.exclude_keyword || UNSET_VALUE;

  const getTaskTargetType = (task) => normalizeTaskType(task.taskType || task.task_type);

  const getTaskTargetValue = (task) => {
    const taskType = getTaskTargetType(task);
    if (taskType === 'seller') {
      return task.seller || UNSET_VALUE;
    }
    return task.keyword || task.keywords || UNSET_VALUE;
  };

  const getTaskFields = (task) => {
    const taskType = getTaskTargetType(task);

    const fields = [
      {
        label: '任务类型',
        value: TASK_TARGETS[taskType].label
      },
      {
        label: TASK_TARGETS[taskType].label,
        value: getTaskTargetValue(task)
      },
      {
        label: '查询间隔',
        value: formatQueryInterval(task)
      },
      {
        label: '到期时间',
        value: task.expired_at ? formatExpiredAt(task.expired_at) : UNSET_VALUE
      }
    ];

    if (taskType === 'keyword') {
      const keywordFields = [
        {
          label: PRICE_RANGE_LABEL,
          value: formatPriceRange(task)
        }
      ];

      if (supportsCategory(task.website)) {
        keywordFields.push({
          label: '分类',
          value: formatCategoryPath(getCategoryId(task))
        });
      }

      if (supportsExcludeKeyword(task.website)) {
        keywordFields.push({
          label: EXCLUDE_KEYWORD_LABEL,
          value: getExcludeKeyword(task),
          clamp: true
        });
      }

      fields.splice(3, 0, ...keywordFields);
    }

    return fields;
  };

  const activeTaskTarget = TASK_TARGETS[editTask.taskType];
  const canEditCategory = editTask.taskType === 'keyword' && supportsCategory(editTask.website);
  const canEditExcludeKeyword = editTask.taskType === 'keyword' && supportsExcludeKeyword(editTask.website);
  const sellerItemIdHelpText = SELLER_ITEM_ID_HELP_TEXT[editTask.website] || '';
  const sellerItemIdHelpId = sellerItemIdHelpText ? 'seller-item-id-help' : undefined;
  const selectedCategoryPath = findCategoryPath(mercariCategories, editTask.categoryId);
  const selectedTopCategory = selectedCategoryPath[0] || null;
  const selectedSecondCategory = selectedCategoryPath[1] || null;
  const selectedThirdCategory = selectedCategoryPath[2] || null;
  const secondLevelCategories = selectedTopCategory?.children || [];
  const thirdLevelCategories = selectedSecondCategory?.children || [];
  const currentRenewalTargetType = currentRenewalTask ? getTaskTargetType(currentRenewalTask) : null;
  const currentRenewalTargetLabel = currentRenewalTargetType ? TASK_TARGETS[currentRenewalTargetType].label : '';
  const currentRenewalTargetValue = currentRenewalTask ? String(getTaskTargetValue(currentRenewalTask)).trim() : '';
  const qrModalContent = qrModalType ? QR_MODAL_CONTENT[qrModalType] : null;
  const websiteTabs = getWebsiteTabs(tasks);
  const couponWebsiteOptions = getCouponWebsiteOptions(tasks);
  const visibleTasks = activeWebsite === null
    ? []
    : tasks.filter(task => getWebsiteKey(task.website) === activeWebsite);

  const handleCategoryChange = (value) => {
    const nextCategoryId = normalizeCategoryId(value);
    setEditTask({ ...editTask, categoryId: nextCategoryId });
  };

  const handleCouponWebsiteKeyDown = (event, index) => {
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!navigationKeys.includes(event.key)) return;

    event.preventDefault();
    let nextIndex = index;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + couponWebsiteOptions.length) % couponWebsiteOptions.length;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % couponWebsiteOptions.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = couponWebsiteOptions.length - 1;
    }
    setCouponWebsite(couponWebsiteOptions[nextIndex]);
  };

  const getOrderedTasks = (taskList) => {
    return taskList
      .map((task, index) => ({
        task,
        index,
        status: getTaskStatus(task)
      }))
      .sort((a, b) => a.status.sortOrder - b.status.sortOrder || a.index - b.index);
  };

  return (
    <div className="task-list-container">
      <div className="task-list-header">
        <h1>任务列表</h1>
        <button className="coupon-exchange-button" onClick={() => handleCouponExchange()}>
          <span>🎫</span>
          <span>券码兑换</span>
        </button>
      </div>
      <p className="task-list-helper">
        <span>还没有券码？</span>
        <button type="button" className="task-list-helper-link" onClick={() => setQrModalType('purchase')}>
          <span aria-hidden="true">🛒</span>
          <span>点击购买</span>
        </button>
        <span>。兑换或使用遇到问题？</span>
        <button type="button" className="task-list-helper-link" onClick={() => setQrModalType('support')}>
          <span aria-hidden="true">💬</span>
          <span>联系客服</span>
        </button>
        <span>。</span>
      </p>
      {!loading && websiteTabs.length > 0 && (
        <div className="website-tabs" role="tablist" aria-label="网站">
          {websiteTabs.map(tab => (
            <button
              key={tab.key || '__empty_website__'}
              type="button"
              role="tab"
              aria-selected={activeWebsite === tab.key}
              className={`website-tab ${activeWebsite === tab.key ? 'active' : ''}`}
              onClick={() => setActiveWebsite(tab.key)}
            >
              <WebsiteLabel website={tab.key} />
            </button>
          ))}
        </div>
      )}
      <div className="task-cards">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>加载中...</p>
          </div>
        ) : visibleTasks.length > 0 ? (
          getOrderedTasks(visibleTasks).map(({ task, status }) => {
            return (
              <div key={task.id} className={`task-card${status.cardClassName}`}>
                <div className="task-status-row">
                  <WebsiteLabel website={task.website} className="task-website-badge" />
                  <span className={`task-status-badge${status.badgeClassName}`}>
                    {status.label}
                  </span>
                </div>
                <div className="task-details">
                  <div className="task-fields">
                    {getTaskFields(task).map((field) => (
                      <div className="task-field" key={field.label}>
                        <span className="task-field-label">{field.label}</span>
                        <span className={`task-field-value${field.value === UNSET_VALUE ? ' task-field-value-muted' : ''}${field.clamp ? ' task-field-value-clamped' : ''}`}>
                          {field.value}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="task-card-actions">
                    <button className="edit-button" onClick={() => handleEdit(task)}>
                      <span>✏️</span>
                      <span>编辑</span>
                    </button>
                    <button className="renew-button" onClick={() => handleCouponExchange(task)}>
                      <span>🔄</span>
                      <span>续费</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-tasks">
            <p>{tasks.length > 0 ? '当前网站暂无任务' : '暂无任务，点击"券码兑换"按钮创建新任务'}</p>
          </div>
        )}
      </div>

      {/* 编辑任务模态框 */}
      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-fixed">
            <div className="modal-header">
              <h3>
                编辑 <WebsiteLabel website={editTask.website} className="website-label-heading" /> 任务
              </h3>
            </div>
            <div className="modal-body">
              <div className="modal-form">
                <div className="form-group">
                  <label>任务类型</label>
                  <div className="task-target-toggle" role="tablist" aria-label="任务类型">
                    {Object.entries(TASK_TARGETS).map(([taskType, target]) => (
                      <button
                        key={taskType}
                        type="button"
                        className={`task-target-toggle-button ${editTask.taskType === taskType ? 'active' : ''}`}
                        onClick={() => setEditTask({ ...editTask, taskType })}
                        role="tab"
                        aria-selected={editTask.taskType === taskType}
                      >
                        {target.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  {editTask.taskType === 'seller' ? (
                    <div className="form-label-row">
                      <label>{activeTaskTarget.label}</label>
                      {sellerItemIdHelpText && (
                        <span className="seller-help-wrapper">
                          <button
                            type="button"
                            className="seller-help-button"
                            aria-label="商品编号规则"
                            aria-describedby={sellerItemIdHelpId}
                            title={sellerItemIdHelpText}
                          >
                            ?
                          </button>
                          <span
                            id={sellerItemIdHelpId}
                            className="seller-help-tooltip"
                            role="tooltip"
                          >
                            {sellerItemIdHelpText}
                          </span>
                        </span>
                      )}
                    </div>
                  ) : (
                    <label>{activeTaskTarget.label}</label>
                  )}
                  {editTask.taskType === 'seller' ? (
                    <div className="seller-lookup-panel">
                      <div className="seller-lookup-row">
                        <input
                          type="text"
                          value={sellerItemId}
                          onChange={(e) => setSellerItemId(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleFetchSellerId()}
                          placeholder="请输入卖家任意商品编号"
                        />
                        <button
                          type="button"
                          className="seller-lookup-button"
                          onClick={handleFetchSellerId}
                          disabled={isFetchingSeller}
                        >
                          {isFetchingSeller ? '获取中...' : '获取'}
                        </button>
                      </div>
                      <div className="readonly-field">
                        {editTask.seller || activeTaskTarget.placeholder}
                      </div>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={editTask.keyword}
                      onChange={(e) => setEditTask({ ...editTask, keyword: e.target.value })}
                      placeholder={activeTaskTarget.placeholder}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>查询间隔</label>
                  <div className="readonly-field">
                    {editTask.speed} 秒
                  </div>
                </div>
                {editTask.taskType === 'keyword' && (
                  <div className="form-group">
                    <label>{PRICE_RANGE_LABEL}</label>
                    <div style={{ display: 'flex', gap: '10px', width: '100%', boxSizing: 'border-box' }}>
                      <input
                        type="number"
                        value={editTask.minPrice || ''}
                        onChange={(e) => setEditTask({ ...editTask, minPrice: e.target.value === '' ? null : e.target.value })}
                        placeholder="最低价格"
                        min="0"
                        step="1"
                        style={{ flex: 1, boxSizing: 'border-box', maxWidth: 'calc(50% - 15px)' }}
                      />
                      <span style={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>-</span>
                      <input
                        type="number"
                        value={editTask.maxPrice || ''}
                        onChange={(e) => setEditTask({ ...editTask, maxPrice: e.target.value === '' ? null : e.target.value })}
                        placeholder="最高价格"
                        min="0"
                        step="1"
                        style={{ flex: 1, boxSizing: 'border-box', maxWidth: 'calc(50% - 15px)' }}
                      />
                    </div>
                  </div>
                )}
                {canEditCategory && (
                  <div className="form-group">
                    <label>分类</label>
                    <div className="category-select-grid">
                      <CategoryDropdown
                        label="一级分类"
                        value={selectedTopCategory?.id}
                        placeholder="不选择分类"
                        options={mercariCategories}
                        onChange={handleCategoryChange}
                      />
                      {selectedTopCategory && secondLevelCategories.length > 0 && (
                        <CategoryDropdown
                          label="二级分类"
                          value={selectedSecondCategory?.id}
                          placeholder="全部"
                          options={secondLevelCategories}
                          onChange={(value) => handleCategoryChange(value || selectedTopCategory.id)}
                        />
                      )}
                      {selectedSecondCategory && thirdLevelCategories.length > 0 && (
                        <CategoryDropdown
                          label="三级分类"
                          value={selectedThirdCategory?.id}
                          placeholder="全部"
                          options={thirdLevelCategories}
                          onChange={(value) => handleCategoryChange(value || selectedSecondCategory.id)}
                        />
                      )}
                    </div>
                  </div>
                )}
                {canEditExcludeKeyword && (
                  <div className="form-group">
                    <label>{EXCLUDE_KEYWORD_LABEL}</label>
                    <textarea
                      value={editTask.excludeKeyword}
                      onChange={(e) => setEditTask({ ...editTask, excludeKeyword: e.target.value })}
                      maxLength={100}
                      placeholder={EXCLUDE_KEYWORD_PLACEHOLDER}
                      rows={3}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSaving}
                  style={{
                    opacity: isSaving ? 0.5 : 1,
                    cursor: isSaving ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>✕</span>
                  <span>取消</span>
                </button>
                <button
                  className="save-button"
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{
                    opacity: isSaving ? 0.7 : 1,
                    cursor: isSaving ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSaving ? (
                    <>
                      <span>⏳</span>
                      <span>保存中...</span>
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      <span>保存</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {qrModalContent && (
        <div className="modal-overlay">
          <div className="modal-content qr-modal-content">
            <h3>{qrModalContent.title}</h3>
            <p className="qr-modal-description">{qrModalContent.description}</p>
            <div className="qr-image-frame">
              <img
                src={qrModalContent.imageSrc}
                alt={qrModalContent.imageAlt}
                decoding="async"
              />
            </div>
            <div className="modal-actions qr-modal-actions">
              <button className="cancel-button" onClick={() => setQrModalType(null)}>
                <span>✕</span>
                <span>关闭</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 券码兑换模态框 */}
      {isCouponModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content coupon-modal-content">
            <h3>{currentRenewalTask ? '续费券码兑换' : '券码兑换'}</h3>
            {currentRenewalTargetValue && currentRenewalTargetValue !== UNSET_VALUE && (
              <p className="coupon-renewal-context">
                正在续费 <WebsiteIcon website={currentRenewalTask.website} className="website-icon-inline" />{getWebsiteLabel(currentRenewalTask.website)} {currentRenewalTargetLabel}：{currentRenewalTargetValue}
              </p>
            )}
            <div className="modal-form">
              {!currentRenewalTask && (
                <div className="form-group">
                  <div
                    className="coupon-website-picker"
                    role="radiogroup"
                    aria-label="平台选择"
                  >
                    {couponWebsiteOptions.map((website, index) => (
                      <button
                        key={website}
                        type="button"
                        className={`coupon-website-option ${couponWebsite === website ? 'active' : ''}`}
                        role="radio"
                        aria-checked={couponWebsite === website}
                        onClick={() => setCouponWebsite(website)}
                        onKeyDown={(event) => handleCouponWebsiteKeyDown(event, index)}
                      >
                        <WebsiteLabel website={website} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group">
                <div className="coupon-query-row">
                  <input
                    type="text"
                    className="coupon-code-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleQueryCoupon()}
                    placeholder="请输入券码，先查询再兑换"
                  />
                  <button
                    type="button"
                    className="query-button"
                    onClick={handleQueryCoupon}
                    disabled={isQuerying}
                  >
                    {isQuerying ? (
                      <>
                        <span>⏳</span>
                        <span>查询中...</span>
                      </>
                    ) : (
                      <>
                        <span>🔍</span>
                        <span>查询</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {couponError && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '14px',
                  backgroundColor: '#fef2f2',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  width: '100%',
                  boxSizing: 'border-box',
                  marginBottom: '12px'
                }}>
                  {couponError}
                </div>
              )}

              {couponInfo && (
                <div style={{
                  width: '100%',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>速度</div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{couponInfo.speed} 秒</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>天数</div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{couponInfo.days} 天</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={() => {
                    setIsCouponModalOpen(false);
                    setCurrentRenewalTask(null);
                  }}
                  disabled={isRedeeming}
                  style={{
                    opacity: isRedeeming ? 0.5 : 1,
                    cursor: isRedeeming ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>✕</span>
                  <span>取消</span>
                </button>
                <button
                  className="save-button"
                  onClick={handleSaveCoupon}
                  disabled={!couponInfo || isRedeeming}
                  style={{
                    opacity: (!couponInfo || isRedeeming) ? 0.7 : 1,
                    cursor: (!couponInfo || isRedeeming) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isRedeeming ? (
                    <>
                      <span>⏳</span>
                      <span>兑换中...</span>
                    </>
                  ) : (
                    <>
                      <span>🎁</span>
                      <span>兑换</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskList;
